import { useState, useEffect } from 'react';
import { getPrivateOfferByToken, acceptPrivateOffer, type PrivateOfferProposal } from '../services/offerService';

interface PrivateOfferModalProps {
  token: string;
  onClose: () => void;
}

export function PrivateOfferModal({ token, onClose }: PrivateOfferModalProps) {
  const [offer, setOffer] = useState<PrivateOfferProposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadOffer() {
      setLoading(true);
      setError(null);
      try {
        const data = await getPrivateOfferByToken(token);
        if (!data) {
          setError('This private proposal link is invalid or does not exist.');
        } else if (data.status === 'expired') {
          setError('This proposal has expired. Please contact support@everforgefitness.com to request an updated offer.');
        } else if (data.status === 'revoked') {
          setError('This proposal is no longer active.');
        } else {
          setOffer(data);
          if (data.status === 'accepted') {
            setIsAccepted(true);
          }
        }
      } catch (err) {
        setError('Failed to load private proposal. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadOffer();
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const ok = await acceptPrivateOffer(token);
      if (ok) {
        setIsAccepted(true);
      } else {
        alert('Could not record acceptance. Please reach out to support@everforgefitness.com.');
      }
    } catch (err) {
      alert('Error recording acceptance.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(7, 19, 47, 0.75)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '20px',
      }}
    >
      <div
        style={{
          background: '#0d131f',
          border: '1.5px solid rgba(255, 255, 255, 0.12)',
          borderRadius: '20px',
          padding: '28px 32px',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '92vh',
          overflowY: 'auto',
          color: '#ffffff',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>🔒</span>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
              Private Coaching Proposal
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              fontSize: '20px',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
            Unlocking confidential proposal...
          </div>
        ) : error ? (
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', borderRadius: '12px', padding: '16px', color: '#fca5a5', fontSize: '13.5px', lineHeight: 1.5 }}>
            {error}
          </div>
        ) : offer ? (
          <div>
            <div style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '16px', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>Prepared Exclusively For:</div>
              <div style={{ fontSize: '18px', fontWeight: 800, marginTop: '2px' }}>{offer.prospectName}</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>{offer.prospectEmail}</div>
            </div>

            {/* Plan Details & Unmasked Pricing */}
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7), rgba(15, 23, 42, 0.9))',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '20px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#38bdf8', fontWeight: 700 }}>
                    Coaching Program
                  </div>
                  <div style={{ fontSize: '17px', fontWeight: 800, marginTop: '2px' }}>{offer.planName}</div>
                </div>
                <span
                  style={{
                    background: 'rgba(56, 189, 248, 0.15)',
                    color: '#38bdf8',
                    border: '1px solid rgba(56, 189, 248, 0.3)',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                  }}
                >
                  {offer.billingCycleMonths} Months Commitment
                </span>
              </div>

              {/* Price Figures */}
              <div style={{ marginTop: '16px', padding: '14px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#94a3b8' }}>Unmasked Monthly Rate:</span>
                  <span style={{ fontSize: '22px', fontWeight: 900, color: '#4ade80' }}>
                    ${offer.finalMonthlyRate} <span style={{ fontSize: '12px', color: '#94a3b8' }}>/ mo</span>
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Total Commitment Due:</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>
                    ${offer.totalCommitmentPrice} Total
                  </span>
                </div>
              </div>

              {/* Privilege tags */}
              <div style={{ marginTop: '12px', fontSize: '11.5px', color: '#94a3b8', lineHeight: 1.5 }}>
                {offer.durationDiscountPercent > 0 && <div>• {offer.durationDiscountPercent}% commitment term savings included</div>}
                {offer.festivalDiscountPercent > 0 && <div>• {offer.festivalDiscountPercent}% seasonal celebratory privilege applied</div>}
                {offer.individualDiscountValue > 0 && (
                  <div>
                    • Personalized Courtesy: {offer.discountReason || 'Individual consultation privilege'}
                  </div>
                )}
              </div>
            </div>

            {/* Acceptance Section or Next Steps */}
            {!isAccepted ? (
              <div>
                <p style={{ fontSize: '12.5px', color: '#94a3b8', lineHeight: 1.5, margin: '0 0 16px 0' }}>
                  By selecting <strong>Accept &amp; Continue</strong>, you formally accept this proposal and its commitment duration. Once accepted, payment instructions (Zelle / bank wire) will be revealed.
                </p>

                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={accepting}
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#16a34a',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)',
                  }}
                >
                  {accepting ? 'Recording Acceptance...' : 'Accept Proposal & View Payment Details →'}
                </button>
              </div>
            ) : (
              <div style={{ background: 'rgba(22, 163, 74, 0.1)', border: '1.5px solid #22c55e', borderRadius: '14px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4ade80', fontWeight: 800, fontSize: '14px', marginBottom: '8px' }}>
                  <span>✅</span>
                  <span>Proposal Accepted</span>
                </div>
                <p style={{ fontSize: '12.5px', color: '#cbd5e1', lineHeight: 1.5, margin: '0 0 12px 0' }}>
                  Thank you, <strong>{offer.prospectName}</strong>! To finalize your enrollment and receive your onboarding invitation:
                </p>

                <div style={{ background: 'rgba(0,0,0,0.3)', padding: '12px 14px', borderRadius: '8px', fontSize: '12.5px', color: '#ffffff', marginBottom: '12px' }}>
                  <div><strong>Payment Method:</strong> Zelle or Direct Bank Transfer</div>
                  <div><strong>Payment Recipient:</strong> <code>support@everforgefitness.com</code></div>
                  <div><strong>Amount Due:</strong> ${offer.totalCommitmentPrice} USD</div>
                  <div><strong>Reference Memo:</strong> Everforge - {offer.prospectName}</div>
                </div>

                <p style={{ fontSize: '11.5px', color: '#94a3b8', margin: 0, lineHeight: 1.4 }}>
                  After submitting transfer, email confirmation to <strong>support@everforgefitness.com</strong>. Your coach will verify payment and email your single-use account registration link.
                </p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

